import {
  AccountType,
  getV1CompaniesCompanyIdOrdersOrderIdSiteRateSchedules,
  getV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedules,
  GetV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedulesData,
  getV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedulesPreviewOrders,
  getV1CompaniesCompanyIdSiteRateSchedulesIdOrders,
  getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments,
  Order_Read_Rate_Impact,
  Order_Read_Typeahead,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { SiteRateSchedule } from './models';

interface GetProjectSiteRateSchedulesParams {
  projectId: string;
  pickupSiteId?: string;
  limit?: number;
  cursor?: string;
}

// Completeness guard: the impact-preview fetchers below drive the single referenced-list
// reconcile — the FE MUST load the COMPLETE in-range order set, because "an order not in the list"
// means the BE dereferences it. A cursor loop that can't exhaust (an endless/looping cursor, a
// server-side cap) must FAIL the save, never return a short list — a truncated referenced list would
// wrongly dereference every order it omits. 50 pages × 100/page = 5000 orders is far beyond any real
// project's in-range set, so hitting it means "can't complete", not "genuinely this large". We THROW
// (never break): the throw flows through the existing fail-closed catch so the caller aborts the save.
const MAX_IMPACT_PAGES = 50;
const MAX_IMPACT_ORDERS = MAX_IMPACT_PAGES * MAX_API_PAGE_SIZE;

export const useSiteRateSchedule = () => {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const getProjectSiteRateSchedules = async ({
    projectId,
    pickupSiteId,
    limit,
    cursor,
  }: GetProjectSiteRateSchedulesParams) => {
    try {
      const query: Partial<
        GetV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedulesData['query']
      > = {
        'page[limit]': limit ?? 20,
        ...(cursor && { 'page[after]': cursor }),
      };

      if (pickupSiteId) {
        query['filter[pickup_site_id]'] = pickupSiteId;
      }

      const response = await getV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedules({
        path: { 'company-id': companyId, 'project-id': projectId },
        query,
      });

      return response.data.data.map(SiteRateSchedule.parse);
    } catch (e) {
      connection.handleRequestError(e, t('error_messages.site.failed_to_fetch'));
    }
  };

  /**
   * Every rate card owned by the project, following cursors to exhaustion. Toasts and
   * re-throws on failure so callers can tell an error from a genuinely empty set.
   */
  const fetchAllProjectSiteRateSchedules = async (
    projectId: string,
    options?: { signal?: AbortSignal },
  ) => {
    try {
      // No MAX_IMPACT_PAGES cap here (unlike the impact-preview fetchers): this loads the project's
      // rate CARDS, not the referenced ORDER set, so its emptiness/length is never load-bearing for
      // the dereference reconcile — a truncated card list can't wrongly dereference orders. Capping
      // would only convert a (theoretical) endless cursor from a visible hang into a silently short
      // list that the swallowing variant (`getAllProjectSiteRateSchedules`) turns into `[]`.
      return await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedules({
            path: { 'company-id': companyId, 'project-id': projectId },
            query,
            signal: pageSignal,
          }),
        SiteRateSchedule.parse,
        { signal: options?.signal },
      );
    } catch (e) {
      if (options?.signal?.aborted) {
        throw e;
      }
      try {
        connection.handleRequestError(e, t('error_messages.site.failed_to_fetch'));
      } catch {
        // Error already handled (toast + Sentry)
      }
      throw e;
    }
  };

  /**
   * Swallowing variant of fetchAllProjectSiteRateSchedules: a failure reads as an empty
   * set. Only for callers that treat the list as optional context, never for ones that
   * branch on emptiness.
   */
  const getAllProjectSiteRateSchedules = async (
    projectId: string,
    options?: { signal?: AbortSignal },
  ) => {
    try {
      return await fetchAllProjectSiteRateSchedules(projectId, options);
    } catch (error) {
      if (options?.signal?.aborted) {
        throw error;
      }

      // Error already handled (toast + Sentry)
      return [];
    }
  };

  /**
   * Every rate card owned by the order, following cursors to exhaustion. Toasts and
   * re-throws on failure so callers can tell an error from a genuinely empty set.
   */
  const fetchAllOrderSiteRateSchedules = async (
    companyId: string,
    orderId: string,
    options?: { signal?: AbortSignal },
  ) => {
    try {
      // No MAX_IMPACT_PAGES cap here — same rationale as fetchAllProjectSiteRateSchedules: this loads
      // the order's rate CARDS, not the referenced ORDER set, so its length isn't load-bearing for the
      // dereference reconcile and capping would only mask an endless cursor as a silently short list.
      return await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdOrdersOrderIdSiteRateSchedules({
            path: { 'company-id': companyId, 'order-id': orderId },
            query,
            signal: pageSignal,
          }),
        SiteRateSchedule.parse,
        { signal: options?.signal },
      );
    } catch (e) {
      if (options?.signal?.aborted) {
        throw e;
      }
      try {
        connection.handleRequestError(e, t('error_messages.site.failed_to_fetch'));
      } catch {
        // Error already handled (toast + Sentry)
      }
      throw e;
    }
  };

  /**
   * Swallowing variant of fetchAllOrderSiteRateSchedules: a failure reads as an empty
   * set. Only for callers that treat the list as optional context, never for ones that
   * branch on emptiness.
   */
  const getAllOrderSiteRateSchedules = async (
    companyId: string,
    orderId: string,
    options?: { signal?: AbortSignal },
  ) => {
    try {
      return await fetchAllOrderSiteRateSchedules(companyId, orderId, options);
    } catch (error) {
      if (options?.signal?.aborted) {
        throw error;
      }

      // Error already handled (toast + Sentry)
      return [];
    }
  };

  const getScheduleAutoAddOns = async (
    companyId: string,
    scheduleId: string,
  ): Promise<AddOnAssignment[]> => {
    try {
      // The BE's auto_add_on_assignments endpoint silently filters to
      // `account_type: nil` (driver-side) when no `filter[account_type]` is
      // passed. SiteRateSchedule is the one resource where a single record
      // genuinely owns add-ons spanning all three sides, so we have to fan out
      // per account type and merge — three calls in parallel cost ~one
      // round-trip and the BE returns the correct `account_type` on each
      // record so `partitionAddOns` can route them per side downstream.
      const path = { 'company-id': companyId, 'site-rate-schedule-id': scheduleId };
      const [driverRes, customerRes, vendorRes] = await Promise.all([
        getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments({
          path,
        }),
        getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments({
          path,
          query: { 'filter[account_type]': AccountType.CUSTOMER },
        }),
        getV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments({
          path,
          query: { 'filter[account_type]': AccountType.VENDOR },
        }),
      ]);

      return [
        ...(driverRes.data.data ?? []),
        ...(customerRes.data.data ?? []),
        ...(vendorRes.data.data ?? []),
      ].map(AddOnAssignment.parse);
    } catch (e) {
      try {
        connection.handleRequestError(e, t('error_messages.site.failed_to_fetch'));
      } catch {
        // Error already handled (toast + Sentry)
      }
      return [];
    }
  };

  const getSiteRateScheduleDefaultOnOrders = async (
    companyId: string,
    siteRateScheduleId: string,
    // Selective rate apply: pass `scope: 'cascade_apply'` from the impact dialog to get the
    // cascade-reachable audience. Omitted (delete-card warning) keeps the legacy starred+incomplete
    // audience — the BE endpoint defaults to legacy so the pre-existing warning is unaffected.
    // `from`/`to` (ISO date) scope the preview to orders loaded within the range; the
    // BE returns only in-range orders and folds the rest into the pin set at apply time.
    // `existenceLimit`: existence-only probe — fetch a single small page instead of
    // paging to exhaustion. Used by the "are ANY orders reachable at all?" dialog-open gate, which
    // only needs `.length > 0`, never the full set. Exhausting the whole historical reachable set
    // there would page up to MAX_IMPACT_PAGES and throw on a project with >5000 past orders,
    // permanently blocking the save for a boolean it doesn't need.
    options?: {
      scope?: 'cascade_apply';
      from?: string;
      to?: string;
      existenceLimit?: number;
      signal?: AbortSignal;
    },
  ): Promise<Order_Read_Typeahead[]> => {
    try {
      // Existence-only probe: one small page, no cursor loop, no cap. Inside the try so a probe
      // failure still routes through the fail-closed handleRequestError + re-throw below.
      if (options?.existenceLimit !== undefined) {
        const response = await getV1CompaniesCompanyIdSiteRateSchedulesIdOrders({
          path: { 'company-id': companyId, id: siteRateScheduleId },
          query: {
            'page[limit]': options.existenceLimit,
            ...(options?.scope ? { scope: options.scope } : {}),
            ...(options?.from ? { from: options.from } : {}),
            ...(options?.to ? { to: options.to } : {}),
          },
          signal: options?.signal,
        });
        return response.data.data;
      }
      return await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdSiteRateSchedulesIdOrders({
            path: { 'company-id': companyId, id: siteRateScheduleId },
            query: {
              ...(options?.scope ? { scope: options.scope } : {}),
              ...(options?.from ? { from: options.from } : {}),
              ...(options?.to ? { to: options.to } : {}),
              ...query,
            },
            signal: pageSignal,
          }),
        (order) => order,
        { maxRecords: MAX_IMPACT_ORDERS, signal: options?.signal },
      );
    } catch (e) {
      if (options?.signal?.aborted) {
        throw e;
      }

      // Fail-closed. Surface the error (toast + Sentry via
      // handleRequestError) and re-throw so the caller can skip the destructive
      // operation. Returning `[]` here would silently let the user delete a
      // rate card without seeing the affected-orders warning when the safety
      // lookup itself failed (TRE-14609).
      try {
        connection.handleRequestError(e, t('error_messages.site.failed_to_fetch'));
      } catch {
        // Error already handled (toast + Sentry)
      }
      throw e;
    }
  };

  // Prospective (id-less) impact preview, consumed by the create path.
  //
  // Called PROJECT-WIDE — no proposed conditions, no `site_rate_schedule_id` — i.e. create-mode with
  // wildcard conditions. It is used only in the FIRST-CARD regime: a card create on a project that has
  // NO persisted rate card yet, where the id-based `cascade_apply` preview has no card id to key to.
  // In that regime the BE's winner-flip diff has an empty `current_set`, so every unstarred
  // cascade-reachable order flips to the prospective card — the returned set is exactly the cascade's
  // real write audience (read == write), omitting only stamped orders (which the cascade protects
  // regardless). When the project already has a card, the caller uses the id-based endpoint instead —
  // wildcards there would (correctly) return only currently-unmatched orders, which is NOT the
  // project-wide set. `from`/`to` (ISO date) scope by `load_at`, matching the id-based preview.
  const getProjectPreviewOrders = async (
    companyId: string,
    projectId: string,
    // `existenceLimit`: existence-only probe — see getSiteRateScheduleDefaultOnOrders.
    // The first-card create path probes reachability through this fetcher, so it needs the same
    // small-page short-circuit to avoid exhausting the historical set just to check `.length > 0`.
    options?: {
      from?: string;
      to?: string;
      existenceLimit?: number;
      signal?: AbortSignal;
    },
  ): Promise<Order_Read_Rate_Impact[]> => {
    try {
      // Existence-only probe: one small page, no cursor loop, no cap. Inside the try so a probe
      // failure still routes through the fail-closed handleRequestError + re-throw below.
      if (options?.existenceLimit !== undefined) {
        const response =
          await getV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedulesPreviewOrders({
            path: { 'company-id': companyId, 'project-id': projectId },
            query: {
              'page[limit]': options.existenceLimit,
              ...(options?.from ? { from: options.from } : {}),
              ...(options?.to ? { to: options.to } : {}),
            },
            signal: options?.signal,
          });
        return response.data.data;
      }
      return await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedulesPreviewOrders({
            path: { 'company-id': companyId, 'project-id': projectId },
            query: {
              ...(options?.from ? { from: options.from } : {}),
              ...(options?.to ? { to: options.to } : {}),
              ...query,
            },
            signal: pageSignal,
          }),
        (order) => order,
        { maxRecords: MAX_IMPACT_ORDERS, signal: options?.signal },
      );
    } catch (e) {
      if (options?.signal?.aborted) {
        throw e;
      }

      // Fail-closed, mirroring getSiteRateScheduleDefaultOnOrders: surface the error and re-throw so
      // the caller aborts the save rather than cascading to an undisclosed set of orders.
      try {
        connection.handleRequestError(e, t('error_messages.site.failed_to_fetch'));
      } catch {
        // Error already handled (toast + Sentry)
      }
      throw e;
    }
  };

  return {
    fetchAllOrderSiteRateSchedules,
    fetchAllProjectSiteRateSchedules,
    getAllOrderSiteRateSchedules,
    getAllProjectSiteRateSchedules,
    getProjectPreviewOrders,
    getProjectSiteRateSchedules,
    getScheduleAutoAddOns,
    getSiteRateScheduleDefaultOnOrders,
  };
};
